import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { FaPlus } from 'react-icons/fa';

/**
 * Componente de cuadrícula del calendario
 */
const CalendarGrid = ({ 
  currentDate, 
  selectedDate, 
  onSelectDate, 
  hasCitasOnDay,
  isAdmin = false,
  isSuperAdmin = false,
  onGoToToday,
  onShowTimeSelector
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Días de la semana
  const weekDays = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
  
  // Generar días del mes actual
  const generateCalendarDays = () => {
    const startOfMonth = currentDate.clone().startOf('month');
    const endOfMonth = currentDate.clone().endOf('month');
    
    // Obtener el primer día de la semana del mes
    const startDay = startOfMonth.clone().startOf('week');
    // Obtener el último día de la semana del mes
    const endDay = endOfMonth.clone().endOf('week');
    
    const days = [];
    let day = startDay.clone();
    
    while (day.isSameOrBefore(endDay, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }
    
    return days;
  };

  // Renderizar días de la semana
  const renderWeekDays = () => {
    return (
      <div className="grid grid-cols-7 mb-2">
        {weekDays.map((day, index) => (
          <div key={index} className="text-center py-2 text-gray-300 font-medium border-b border-gray-700">
            {day.charAt(0).toUpperCase() + day.slice(1)}
          </div>
        ))}
      </div>
    );
  };

  // Renderizar días del calendario
  const renderCalendarDays = () => {
    const days = generateCalendarDays();
    
    return (
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          const formattedDate = day.format('YYYY-MM-DD');
          const isCurrentMonth = day.month() === currentDate.month();
          const isToday = day.isSame(moment(), 'day');
          const isSelected = selectedDate && day.isSame(selectedDate, 'day');
          
          // Verificar si hay citas para este día
          // Pasamos el objeto day directamente para que la función hasCitasOnDay pueda formatearlo
          const citasInfo = hasCitasOnDay(day);
          
          const isPastDay = day.isBefore(moment(), 'day'); // Verificar si es un día pasado
          
          // Determinar clases según el estado
          let dayClasses = "p-2 border border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors";
          
          // Día seleccionado
          if (isSelected) {
            dayClasses += " bg-blue-600 text-white border-blue-500";
          }
          // Día actual
          else if (isToday) {
            dayClasses += " bg-blue-900 text-white border-blue-700";
          }
          // Día de otro mes
          else if (!isCurrentMonth) {
            dayClasses += " text-gray-600 bg-gray-900 opacity-60";
          }
          // Día normal del mes actual
          else {
            dayClasses += " hover:bg-gray-700 bg-gray-800";
          }
          
          return (
            <div 
              key={formattedDate} 
              className={`${dayClasses} ${isPastDay ? 'opacity-70' : ''}`}
              onClick={() => isCurrentMonth && onSelectDate(day)}
              title={isPastDay ? 'Solo podrás ver las citas pasadas, no crear nuevas' : ''}
              data-date={formattedDate} // Agregar atributo data para depuración
            >
              <span className={`text-sm ${citasInfo ? 'font-bold' : ''}`}>
                {day.date()}
              </span>
              {citasInfo && (
                <div className="flex space-x-1 mt-1">
                  {/* Para clientes: solo punto verde genérico */}
                  {!isAdmin && !isSuperAdmin && (
                    <div className="w-2 h-2 bg-green-500 rounded-full" 
                         title="Hay citas programadas para este día"></div>
                  )}
                  
                  {/* Para admin/superadmin: puntos diferenciados */}
                  {(isAdmin || isSuperAdmin) && typeof citasInfo === 'object' && (
                    <>
                      {citasInfo.hasCompleted && (
                        <div className="w-2 h-2 bg-green-500 rounded-full" 
                             title="Hay citas completadas este día"></div>
                      )}
                      {citasInfo.hasPending && (
                        <div className="w-2 h-2 bg-yellow-500 rounded-full" 
                             title="Hay citas pendientes este día"></div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 rounded-lg p-3 border border-gray-700 shadow-md">
      {renderWeekDays()}
      {renderCalendarDays()}
    
      <div className={`mt-4 flex ${isMobile ? 'justify-between' : 'justify-center'} gap-3`}>
        <button 
          onClick={onGoToToday}
          className="py-2 px-4 bg-gray-700 hover:bg-blue-600 rounded-lg text-sm font-medium transition-colors duration-200"
        >
          Hoy
        </button>
        
        {isMobile && onShowTimeSelector && (
          <button 
            onClick={onShowTimeSelector}
            className="py-2 px-4 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
          >
            <FaPlus size={12} />
            Crear cita
          </button>
        )}
      </div>
    </div>
  );
};

export default CalendarGrid;
